<script setup lang="ts">
import { ref, computed } from 'vue'
import { useInviteStore } from '../../stores/inviteStore'
import { useChatNotificationStore } from '../../stores/chatNotificationStore'
import { useUiStore } from '../../stores/uiStore'

const inviteStore = useInviteStore()
const chatNotificationStore = useChatNotificationStore()
const uiStore = useUiStore()
const op = ref()

const toggle = (event: any) => {
    op.value.toggle(event)
}

const invites = computed(() => inviteStore.myInvites)
const unreads = computed(() => chatNotificationStore.unreads)
const errors = computed(() => uiStore.errors)
const totalCount = computed(() => invites.value.length + chatNotificationStore.totalUnread + errors.value.length)

const loadingId = ref<number | null>(null)
const clearingAll = ref(false)

const emit = defineEmits(['accepted', 'navigate-to-song', 'navigate-to-band'])

const accept = async (id: number, bandName: string) => {
    loadingId.value = id
    try {
        await inviteStore.acceptInvite(id)
        op.value.hide()
        emit('accepted', bandName)
    } finally {
        loadingId.value = null
    }
}

const decline = async (id: number) => {
    loadingId.value = id
    try {
        await inviteStore.declineInvite(id)
        op.value.hide()
    } finally {
        loadingId.value = null
    }
}

const goToBand = async (bandId: number) => {
    await chatNotificationStore.markBandChatAsRead(bandId)
    op.value.hide()
    emit('navigate-to-band', bandId)
}

const goToSong = async (bandId: number, songId: number) => {
    await chatNotificationStore.markSongChatAsRead(bandId, songId)
    op.value.hide()
    emit('navigate-to-song', bandId, songId)
}

const clearAll = async () => {
    clearingAll.value = true
    try {
        // Decline all invites
        for (const invite of [...invites.value]) {
            await inviteStore.declineInvite(invite.id)
        }
        // Mark all chats as read
        await chatNotificationStore.markAllAsRead()
        // Clear all error notifications
        uiStore.clearErrors()
        op.value.hide()
    } finally {
        clearingAll.value = false
    }
}
</script>

<template>
    <div class="relative flex items-center justify-center mr-2">
        <Button severity="secondary" text rounded class="p-2 group hover:bg-slate-100 transition-all duration-200" @click="toggle">
            <i class="pi pi-bell text-xl text-slate-400 group-hover:text-slate-900 transition-colors duration-200"></i>
            <span v-if="totalCount > 0"
                  class="absolute top-1 right-1 flex h-3 w-3">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-3 w-3 bg-red-600 border-2 border-white"></span>
            </span>
        </Button>

        <Popover ref="op">
            <div class="w-80">
                <div class="flex items-center justify-between mb-4 px-2 pt-2">
                    <span class="font-bold text-slate-900">Notifications</span>
                    <span class="text-xs text-slate-400">{{ totalCount }} New</span>
                </div>

                <div v-if="totalCount > 0" class="flex flex-col gap-1 max-h-80 overflow-y-auto p-1">
                    <!-- Invite Notifications -->
                    <div v-for="invite in invites" :key="'invite-' + invite.id"
                         class="p-3 bg-slate-50 rounded-xl border border-slate-100 mb-2 last:mb-0">
                        <p class="text-sm text-slate-700 leading-tight mb-3">
                            <span class="font-bold text-slate-900">{{ invite.senderName }}</span> invited you to join
                            <span class="font-bold text-teal-600">{{ invite.bandName }}</span>.
                        </p>
                        <div class="flex gap-2">
                            <Button label="Accept" severity="success" size="small" class="flex-1 py-1" @click="accept(invite.id, invite.bandName)" :loading="loadingId === invite.id" :disabled="loadingId !== null && loadingId !== invite.id" />
                            <Button label="Decline" severity="secondary" size="small" outlined class="flex-1 py-1" @click="decline(invite.id)" :loading="loadingId === invite.id" :disabled="loadingId !== null && loadingId !== invite.id" />
                        </div>
                    </div>

                    <!-- Error Notifications -->
                    <div v-for="err in errors" :key="'err-' + err.id"
                         class="p-3 bg-red-50 rounded-xl border border-red-100 mb-2 last:mb-0">
                        <div class="flex items-start gap-2">
                            <i class="pi pi-exclamation-circle text-red-500 text-sm mt-0.5"></i>
                            <div class="flex-1 min-w-0">
                                <p class="text-sm font-bold text-slate-900 leading-tight">{{ err.summary }}</p>
                                <p v-if="err.detail" class="text-xs text-slate-600 mt-1 break-words">{{ err.detail }}</p>
                            </div>
                            <button
                                class="w-5 h-5 rounded-full flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-100 transition-colors shrink-0"
                                @click="uiStore.dismissError(err.id)"
                                v-tooltip.bottom="'Dismiss'"
                            >
                                <i class="pi pi-times text-[10px]"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Chat Unread Notifications -->
                    <template v-for="band in unreads" :key="'band-chat-' + band.bandId">
                        <div v-if="band.unreadCount > 0"
                             class="p-3 bg-teal-50 rounded-xl border border-teal-100 mb-2 last:mb-0">
                            <div class="flex items-start gap-2">
                                <i class="pi pi-comments text-teal-500 text-sm mt-0.5"></i>
                                <div class="flex-1">
                                    <p class="text-sm text-slate-700 leading-tight">
                                        You have <span class="font-bold text-teal-600">{{ band.unreadCount }}</span>
                                        new message{{ band.unreadCount > 1 ? 's' : '' }} in the
                                        <span class="font-bold text-slate-900">{{ band.bandName }}</span> band
                                    </p>
                                    <button
                                        class="mt-2 w-6 h-6 rounded-full flex items-center justify-center text-teal-600 hover:text-teal-800 hover:bg-teal-100 transition-colors"
                                        @click="goToBand(band.bandId)"
                                        v-tooltip.bottom="'Go to band'"
                                    >
                                        <i class="pi pi-arrow-right text-xs"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-for="song in (band.songs || [])" :key="'song-chat-' + song.songId"
                             class="p-3 bg-teal-50 rounded-xl border border-teal-100 mb-2 last:mb-0">
                            <div class="flex items-start gap-2">
                                <i class="pi pi-comments text-teal-500 text-sm mt-0.5"></i>
                                <div class="flex-1">
                                    <p class="text-sm text-slate-700 leading-tight">
                                        You have <span class="font-bold text-teal-600">{{ song.unreadCount }}</span>
                                        new message{{ song.unreadCount > 1 ? 's' : '' }} in the
                                        <span class="font-bold text-slate-900">{{ song.songTitle }}</span> song
                                    </p>
                                    <button
                                        class="mt-2 w-6 h-6 rounded-full flex items-center justify-center text-teal-600 hover:text-teal-800 hover:bg-teal-100 transition-colors"
                                        @click="goToSong(band.bandId, song.songId)"
                                        v-tooltip.bottom="'Go to song'"
                                    >
                                        <i class="pi pi-arrow-right text-xs"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
                <div v-if="totalCount > 0" class="px-2 pt-2 pb-1">
                    <Button label="Clear All" icon="pi pi-times" severity="secondary" text size="small" class="w-full" :loading="clearingAll" @click="clearAll" />
                </div>
                <div v-if="totalCount === 0" class="p-8 text-center">
                    <i class="pi pi-inbox text-3xl text-slate-200 mb-2"></i>
                    <p class="text-xs text-slate-400 italic">No new notifications</p>
                </div>
            </div>
        </Popover>
    </div>
</template>

